import React, { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useToast } from '../providers/ToastProvider';
import { TransportClient } from '@peercolab/engine';
import { ChatInstruction, PeerColabAI, ProcessChatInstructionOutput } from '../peer-colab-model/interceptors/peercolab/PeerColabOperations';

interface AISuggestionsProps {
  client: TransportClient;
  usageInstructions: string;
  currentSnapshot: string;
  developerMessage?: string;
  buttonLabel?: string;
  onOperationsApplied: (output: ProcessChatInstructionOutput) => void;
}

const AISuggestions: React.FC<AISuggestionsProps> = ({ client, usageInstructions, currentSnapshot, developerMessage, onOperationsApplied, buttonLabel = 'AI Assist' }) => {
  const { onError } = useToast();
  const [input, setInput] = useState('');
  const [agentMessage, setAgentMessage] = useState('');
  const [messages, setMessages] = useState<ChatInstruction[]>(developerMessage ? [{ type: 'message', role: 'developer', content: developerMessage }] : []);
  const [loading, setLoading] = useState(false);

  async function aiSuggestOperations() {
    setLoading(true);
    // Aggregate conversation
    const currentMessages = [...messages];
    if (currentMessages.length === 0 && developerMessage) {
      currentMessages.push({ type: 'message', role: 'developer', content: developerMessage })
    }
    currentMessages.push({ type: 'message', role: 'user', content: input })

    // Call the PeerColab chat instructions API
    const result = onError((await client.request(PeerColabAI.processChatInstructions({
      currentStateSnapshot: currentSnapshot,
      usageInstructions: usageInstructions,
      items: currentMessages
    }))))

    if (!result.success) {
      setLoading(false);
      return;
    }

    // Run returned agent operations
    for (const operation of result?.value?.operations || []) {
      if (!onError((await client.acceptOperation(operation))).success) {
        break
      }
    }
    if (result?.value?.message) {
      currentMessages.push({ type: 'message', role: 'assistant', content: result?.value?.message || '' });
    }
    if (result.value.operations.length > 0) {
      currentMessages.push({ type: 'message', role: 'developer', content: 'operations performed: ' + JSON.stringify(result.value.operations) });
    }
    setInput('');
    setMessages(currentMessages);
    setAgentMessage(result?.value?.message || '');
    setLoading(false);
    onOperationsApplied(result!.value!);
  }

  return (
    <Box sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, width: '100%' }}>
        <TextField
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              aiSuggestOperations();
            }
          }}
          placeholder="Type your prompt..."
          size="small"
          sx={{ flex: 1 }}
        />
        <Button variant="contained" sx={{ whiteSpace: 'nowrap', minWidth: 100 }} onClick={aiSuggestOperations} disabled={loading}>
          {loading ? <CircularProgress size={22} color="inherit" /> : buttonLabel}
        </Button>
      </Box>
      {agentMessage && (
        <Box sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
          {agentMessage}
        </Box>
      )}
    </Box>
  );
};

export default AISuggestions;
